import React, { Component } from 'react';
import { Alert, Spinner, Table, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PageStatus } from 'enums';
import { Show } from 'Layout';
import { PartnersAPI } from '../../API';
import GridContainer from '../Grid/GridContainer';
import CardHeader from '../Card/CardHeader';
import moment from 'moment';

type Partner = {
  id: string;
  name: string;
  description: string;
  successUrl: string;
  createdAt: string;
  updatedAt: string;
};

type SurveyRow = {
  id: string;
  survey?: { id: string; name: string };
  surveyId?: string;
  createdAt?: string;
};

type State = {
  status: PageStatus;
  error: string | null;
  partners: Partner[];
  expandedId: string | null;
  surveys: SurveyRow[];
  surveysStatus: PageStatus;
};

export class PartnerClientAnalysis extends Component<any, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      status: PageStatus.None,
      error: null,
      partners: [],
      expandedId: null,
      surveys: [],
      surveysStatus: PageStatus.None,
    };
  }

  componentDidMount() {
    this.fetchPartners();
  }

  fetchPartners() {
    Promise.resolve()
      .then(() => this.setState({ status: PageStatus.Loading }))
      .then(() => PartnersAPI.getPartners(10000))
      .then((partners) => {
        this.setState({ partners: partners || [], status: PageStatus.Loaded });
      })
      .catch((error) => {
        this.setState({ status: PageStatus.Error, error: error.message });
      });
  }

  togglePartner(id: string) {
    if (this.state.expandedId === id) {
      this.setState({ expandedId: null, surveys: [] });
      return;
    }
    this.setState({ expandedId: id, surveys: [], surveysStatus: PageStatus.Loading });
    PartnersAPI.getAllPartnerSurveys(id)
      .then((surveys) => {
        this.setState({ surveys: surveys || [], surveysStatus: PageStatus.Loaded });
      })
      .catch(() => {
        this.setState({ surveys: [], surveysStatus: PageStatus.Loaded });
      });
  }

  render() {
    const { status, error, partners, expandedId, surveys, surveysStatus } = this.state;

    return (
      <>
        <GridContainer>
          <div className="w-100 mb-3">
            <CardHeader color="primary">
              <div className="d-flex align-items-center justify-content-between">
                <h4>Partner Client Analysis</h4>
              </div>
            </CardHeader>
          </div>
        </GridContainer>

        <div className="jumbotron bg-white p-3 border shadow-sm">
          <Alert variant="danger" show={status === PageStatus.Error}>
            {error}
          </Alert>

          <Show when={status === PageStatus.Loading}>
            <div className="d-flex justify-content-center w-100 p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          </Show>

          <Show when={status === PageStatus.Loaded && partners.length === 0}>
            <Alert variant="info">No partner client analysis data available yet.</Alert>
          </Show>

          <Show when={status === PageStatus.Loaded && partners.length > 0}>
            <Table responsive size="sm" bordered>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Partner Name</th>
                  <th>Description</th>
                  <th>Success URL</th>
                  <th>Created At</th>
                  <th>Surveys</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner, index) => (
                  <React.Fragment key={partner.id}>
                    <tr>
                      <td>{index + 1}</td>
                      <td>
                        <span
                          dangerouslySetInnerHTML={{ __html: partner.name || '—' }}
                        />
                      </td>
                      <td>{partner.description || '—'}</td>
                      <td>
                        {partner.successUrl
                          ? <a href={partner.successUrl} target="_blank" rel="noreferrer" className="text-primary">{partner.successUrl}</a>
                          : '—'}
                      </td>
                      <td>{moment(partner.createdAt).format('MM/DD/YYYY HH:mm A')}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => this.togglePartner(partner.id)}
                        >
                          <FontAwesomeIcon
                            icon={['fas', expandedId === partner.id ? 'chevron-up' : 'chevron-down']}
                            className="mr-1"
                          />
                          {expandedId === partner.id ? 'Hide' : 'View'} Surveys
                        </Button>
                      </td>
                    </tr>

                    {expandedId === partner.id && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <div className="p-3 bg-light border-top">
                            {surveysStatus === PageStatus.Loading && (
                              <div className="d-flex justify-content-center p-3">
                                <Spinner animation="border" variant="primary" size="sm" />
                              </div>
                            )}
                            {surveysStatus === PageStatus.Loaded && surveys.length === 0 && (
                              <Alert variant="info" className="mb-0">No surveys assigned to this partner yet.</Alert>
                            )}
                            {surveysStatus === PageStatus.Loaded && surveys.length > 0 && (
                              <Table responsive size="sm" bordered className="mb-0">
                                <thead>
                                  <tr>
                                    <th>S.No</th>
                                    <th>Survey Name</th>
                                    <th>Survey ID</th>
                                    <th>Assigned At</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {surveys.map((row, i) => (
                                    <tr key={row.id || i}>
                                      <td>{i + 1}</td>
                                      <td>{row.survey?.name || '—'}</td>
                                      <td>{row.survey?.id || row.surveyId || '—'}</td>
                                      <td>{row.createdAt ? moment(row.createdAt).format('MM/DD/YYYY HH:mm A') : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </Show>
        </div>
      </>
    );
  }
}
